"use client";

import { c as _c } from "react/compiler-runtime";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getTimeAgoShort } from "@/helpers/Helpers";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import DropCurationButton from "../DropCurationButton";
import WaveDropActions from "../WaveDropActions";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import WaveDropContent from "../WaveDropContent";
import WaveDropMetadata from "../WaveDropMetadata";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropReactions from "../WaveDropReactions";
import ParticipationIdentityProfileCard from "./ParticipationIdentityProfileCard";
import { getParticipationIdentityProfile, getParticipationVisibleMetadata } from "./participationIdentityProfile.helpers";
import type { DropInteractionParams } from "../drop.types";
import { DropLocation } from "../drop.types";
interface EndedParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
export default function EndedParticipationDrop(t0) {
  const $ = _c(99);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick
  } = t0;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const router = useRouter();
  let identityProfile;
  let t1;
  let visibleMetadata;
  if ($[0] !== drop.author || $[1] !== drop.metadata || $[2] !== drop.wave) {
    identityProfile = getParticipationIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    let t2;
    if ($[6] !== drop.metadata || $[7] !== drop.wave) {
      t2 = getParticipationVisibleMetadata({
        wave: drop.wave,
        metadata: drop.metadata
      });
      $[6] = drop.metadata;
      $[7] = drop.wave;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    visibleMetadata = t2;
    t1 = identityProfile ? areSameProfileIdentity({
      left: drop.author,
      right: identityProfile
    }) : false;
    $[0] = drop.author;
    $[1] = drop.metadata;
    $[2] = drop.wave;
    $[3] = identityProfile;
    $[4] = t1;
    $[5] = visibleMetadata;
  } else {
    identityProfile = $[3];
    t1 = $[4];
    visibleMetadata = $[5];
  }
  const isSelfNominee = t1;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const isMobile = useIsMobileDevice();
  const hasTouch = useIsTouchDevice() || isMobile;
  let t2;
  if ($[9] !== router) {
    t2 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[9] = router;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const handleNavigation = t2;
  let t3;
  if ($[11] !== hasTouch) {
    t3 = () => {
      if (!hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[11] = hasTouch;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleLongPress = t3;
  let t4;
  if ($[13] !== activePartIndex || $[14] !== drop || $[15] !== onReply) {
    t4 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[13] = activePartIndex;
    $[14] = drop;
    $[15] = onReply;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const handleOnReply = t4;
  let t5;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsSlideUp(false);
    };
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleOnAddReaction = t5;
  const getDropLocationBackground = _temp;
  const dropBackgroundClass = isActiveDrop ? "tw-bg-[#3CCB7F]/10" : getDropLocationBackground();
  const t6 = `${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""} tw-w-full`;
  const t7 = `tw-group tw-relative tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-px-4 tw-py-3 tw-transition-colors tw-duration-200 tw-ease-linear ${dropBackgroundClass}`;
  let t8;
  if ($[18] !== activePartIndex || $[19] !== drop || $[20] !== handleOnReply || $[21] !== isMobile || $[22] !== showReplyAndQuote) {
    t8 = !isMobile && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} />;
    $[18] = activePartIndex;
    $[19] = drop;
    $[20] = handleOnReply;
    $[21] = isMobile;
    $[22] = showReplyAndQuote;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== drop) {
    t9 = <WaveDropAuthorPfp drop={drop} />;
    $[24] = drop;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== drop.author.level) {
    t10 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[26] = drop.author.level;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== drop.author.handle || $[29] !== drop.author.primary_address || $[30] !== handleNavigation) {
    t11 = e_0 => handleNavigation(e_0, `/${drop.author.handle ?? drop.author.primary_address}`);
    $[28] = drop.author.handle;
    $[29] = drop.author.primary_address;
    $[30] = handleNavigation;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const t12 = `/${drop.author.handle ?? drop.author.primary_address}`;
  const t13 = drop.author.handle ?? drop.author.primary_address;
  let t14;
  if ($[32] !== t11 || $[33] !== t12 || $[34] !== t13) {
    t14 = <p className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none"><Link onClick={t11} href={t12} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{t13}</Link></p>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" />;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== drop.created_at) {
    t16 = getTimeAgoShort(drop.created_at);
    $[37] = drop.created_at;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t16) {
    t17 = <p className="tw-mb-0 tw-whitespace-nowrap tw-text-xs tw-font-normal tw-leading-none tw-text-iron-500">{t16}</p>;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t10 || $[42] !== t14 || $[43] !== t17) {
    t18 = <div className="tw-flex tw-items-center tw-gap-x-2">{t10}{t14}{t15}{t17}</div>;
    $[41] = t10;
    $[42] = t14;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="tw-flex tw-w-fit tw-items-center tw-whitespace-nowrap tw-rounded-md tw-border tw-border-solid tw-border-iron-500/25 tw-bg-iron-600/10 tw-px-2 tw-py-0.5 tw-font-medium tw-text-iron-500"><span className="tw-text-xs">Participant</span></div>;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t18) {
    t20 = <div className="tw-flex tw-flex-col tw-gap-y-2">{t18}{t19}</div>;
    $[46] = t18;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== drop.wave || $[49] !== handleNavigation || $[50] !== showWaveInfo) {
    t21 = showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const isDirectMessage = waveMeta?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link href={waveHref} onClick={e_1 => handleNavigation(e_1, waveHref)} className="tw-leading-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[48] = drop.wave;
    $[49] = handleNavigation;
    $[50] = showWaveInfo;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== activePartIndex || $[53] !== drop || $[54] !== handleLongPress || $[55] !== hasTouch || $[56] !== onDropContentClick || $[57] !== onQuoteClick) {
    t22 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} hasTouch={hasTouch} />;
    $[52] = activePartIndex;
    $[53] = drop;
    $[54] = handleLongPress;
    $[55] = hasTouch;
    $[56] = onDropContentClick;
    $[57] = onQuoteClick;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== t20 || $[60] !== t21 || $[61] !== t22) {
    t23 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-2">{t20}{t21}{t22}</div>;
    $[59] = t20;
    $[60] = t21;
    $[61] = t22;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== t23 || $[64] !== t9) {
    t24 = <div className="tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t9}{t23}</div>;
    $[63] = t23;
    $[64] = t9;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== drop.id || $[67] !== identityProfile || $[68] !== isSelfNominee) {
    t25 = identityProfile && <div className="tw-ml-[3.25rem]"><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant="chat" showIdentityHeader={!isSelfNominee} /></div>;
    $[66] = drop.id;
    $[67] = identityProfile;
    $[68] = isSelfNominee;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] !== visibleMetadata) {
    t26 = visibleMetadata.length > 0 && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><WaveDropMetadata metadata={visibleMetadata} /></div>;
    $[70] = visibleMetadata;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  const t27 = drop.context_profile_context?.curatable ?? false;
  const t28 = drop.context_profile_context?.curated ?? false;
  let t29;
  if ($[72] !== drop.id || $[73] !== drop.wave.id || $[74] !== t27 || $[75] !== t28) {
    t29 = <DropCurationButton dropId={drop.id} waveId={drop.wave.id} isCuratable={t27} isCurated={t28} />;
    $[72] = drop.id;
    $[73] = drop.wave.id;
    $[74] = t27;
    $[75] = t28;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  let t30;
  if ($[77] !== drop) {
    t30 = <WaveDropReactions drop={drop} />;
    $[77] = drop;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  let t31;
  if ($[79] !== t29 || $[80] !== t30) {
    t31 = <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t29}{t30}</div>;
    $[79] = t29;
    $[80] = t30;
    $[81] = t31;
  } else {
    t31 = $[81];
  }
  let t32;
  if ($[82] !== drop || $[83] !== handleOnReply || $[84] !== isSlideUp || $[85] !== longPressTriggered || $[86] !== showReplyAndQuote) {
    t32 = <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[82] = drop;
    $[83] = handleOnReply;
    $[84] = isSlideUp;
    $[85] = longPressTriggered;
    $[86] = showReplyAndQuote;
    $[87] = t32;
  } else {
    t32 = $[87];
  }
  let t33;
  if ($[88] !== t24 || $[89] !== t25 || $[90] !== t26 || $[91] !== t31 || $[92] !== t32 || $[93] !== t7 || $[94] !== t8) {
    t33 = <div className={t7}>{t8}{t24}{t25}{t26}{t31}{t32}</div>;
    $[88] = t24;
    $[89] = t25;
    $[90] = t26;
    $[91] = t31;
    $[92] = t32;
    $[93] = t7;
    $[94] = t8;
    $[95] = t33;
  } else {
    t33 = $[95];
  }
  let t34;
  if ($[96] !== t33 || $[97] !== t6) {
    t34 = <div className={t6}>{t33}</div>;
    $[96] = t33;
    $[97] = t6;
    $[98] = t34;
  } else {
    t34 = $[98];
  }
  return t34;
}
function _temp() {
  return "tw-bg-iron-950 tw-ring-1 tw-ring-inset tw-ring-iron-800";
}